import { useDispatch, useSelector } from 'react-redux';
import { DropDownButton, DropdownItem } from '@optimus-web/core';
import { LocalizedPercent, LocalizedText, useFeatureFlag } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { ApplicationState, WithServiceId } from '@mews-commander/types';
import { TimeUnit } from '@mews/enums';
import { DENSITY_PERCENTAGE, DENSITY, DENSITY_TEXT_KEYS, RESOURCE_LIMIT_FOR_COMPACT_VIEW } from '../../constants';
import { changeDensity } from '../../actions';
import { getSelectedDensity, getSelectedTimeUnit } from '../../selectors/view';
import { getResourcesIncludingNonCategory } from '../../reducers';
import { useTimelineFilterValuesContext } from '../../contexts/timelineFilterContext';

export const DensitySelect = ({ serviceId }: WithServiceId) => {
    const isPercentageDensityValuesEnabled = useFeatureFlag(FeatureFlag.percentageDensityValuesEnabled);
    const withVerticalLazyLoading = useFeatureFlag(FeatureFlag.verticalLazyLoadingTimelineEnabled);
    const dispatch = useDispatch();
    const value = useSelector(getSelectedDensity);
    const filterValues = useTimelineFilterValuesContext();
    const resources = useSelector((state: ApplicationState) =>
        getResourcesIncludingNonCategory(state, { serviceId, filterValues })
    );
    const timeUnit = useSelector(getSelectedTimeUnit);

    const handleClick = (density: number | string) => {
        dispatch(changeDensity(density));
    };

    const dropdownLabel = isPercentageDensityValuesEnabled ? (
        <LocalizedPercent percent={value} options={{ precision: 0 }} />
    ) : (
        <LocalizedText textKey={DENSITY_TEXT_KEYS[value]} />
    );

    const dropdownItems = isPercentageDensityValuesEnabled ? (
        <>
            {Object.values(DENSITY_PERCENTAGE)
                .sort((a, b) => b - a)
                .map((density) => (
                    <DropdownItem
                        key={density}
                        onClick={() => handleClick(density)}
                        label={<LocalizedPercent percent={density} options={{ precision: 0 }} />}
                        selected={value === density}
                    />
                ))}
        </>
    ) : (
        <>
            {Object.values(DENSITY)
                .filter((density) =>
                    resources.length > RESOURCE_LIMIT_FOR_COMPACT_VIEW &&
                    withVerticalLazyLoading &&
                    timeUnit === TimeUnit.Month
                        ? density !== DENSITY.DENSE
                        : true
                )
                .map((density) => (
                    <DropdownItem
                        key={density}
                        onClick={() => handleClick(density)}
                        label={<LocalizedText textKey={DENSITY_TEXT_KEYS[density]} />}
                        selected={value === density}
                    />
                ))}
        </>
    );

    return (
        <DropDownButton data-test-id="timeline-density-selector" label={dropdownLabel} size="medium">
            {dropdownItems}
        </DropDownButton>
    );
};
